export interface AssignmentRuleContext {
  inspectionLevel?: string;
  splitType?: string;
  /**
   * Split types tried, in order, ONLY when neither `splitType` nor an earlier
   * fallback matches any rule — evaluated before `defaultQuestionnaireId`. This
   * lets a half/quarter buyer on a `half_and_two_quarters` layout fall back to
   * their own portion size ('half' / 'quarters') when the processor defined no
   * h2q-specific rule; without it they'd hit the (typically whole) default and
   * be stamped the wrong-size questionnaire.
   */
  fallbackSplitTypes?: string[];
  /** 0-based index of this portion of the animal (e.g. 0 = front quarter, 1 = back quarter). */
  splitIndex?: number;
  producerId?: string;
  buyerId?: string;
}

export interface PlainAssignmentRule {
  sortOrder: number;
  label?: string;
  inspectionLevel?: string[];
  splitType?: string[];
  /** 0-based indices of the portions of the animal to match. Only meaningful for Quarters / Half+Two Quarters. */
  splitIndex?: number[];
  /** ObjectIds as strings — matches when the context producerId is any of these */
  producer?: string[];
  /** ObjectIds as strings — matches when the context buyerId is any of these */
  buyer?: string[];
  /** ObjectId as string */
  questionnaireId: string;
}

export interface PlainAssignmentRuleSet {
  rules: PlainAssignmentRule[];
  /** Fallback when no rule matches */
  defaultQuestionnaireId?: string;
}

function ruleMatches(
  rule: PlainAssignmentRule,
  ctx: AssignmentRuleContext,
  opts?: { ignoreAudience?: boolean },
): boolean {
  if (
    rule.inspectionLevel?.length &&
    (!ctx.inspectionLevel ||
      !rule.inspectionLevel.includes(ctx.inspectionLevel))
  ) {
    return false;
  }
  if (
    rule.splitType?.length &&
    (!ctx.splitType || !rule.splitType.includes(ctx.splitType))
  ) {
    return false;
  }
  if (
    rule.splitIndex?.length &&
    (ctx.splitIndex == null || !rule.splitIndex.includes(ctx.splitIndex))
  ) {
    return false;
  }
  // `ignoreAudience` skips the producer/buyer (audience) filters — used only by
  // the size-safe rescue pass in `resolveQuestionnaire`. The size/inspection
  // filters above still apply.
  if (!opts?.ignoreAudience) {
    if (
      rule.producer?.length &&
      (!ctx.producerId || !rule.producer.includes(ctx.producerId))
    ) {
      return false;
    }
    if (
      rule.buyer?.length &&
      (!ctx.buyerId || !rule.buyer.includes(ctx.buyerId))
    ) {
      return false;
    }
  }
  return true;
}

/**
 * A rule is "size-scoped" when it constrains the split size — a `splitType`
 * and/or `splitIndex` filter. Only these rules carry size information, so only
 * they are eligible for the audience-relaxed rescue pass (an audience-only rule
 * says nothing about size and must keep its producer/buyer scoping).
 * @param rule
 */
function isSizeScoped(rule: PlainAssignmentRule): boolean {
  return !!(rule.splitType?.length || rule.splitIndex?.length);
}

/**
 * Evaluates a CutsheetAssignmentRuleSet against an order context and returns
 * the questionnaireId that should be used.
 *
 * Rules are evaluated in ascending sortOrder. The first rule whose every
 * specified filter matches the context wins. A rule with no filters is a
 * catch-all and always matches.
 *
 * If no rule matches, `defaultQuestionnaireId` is returned. If there is no
 * default either, `undefined` is returned.
 * @param ruleSet
 * @param ctx
 */
export function resolveQuestionnaire(
  ruleSet: PlainAssignmentRuleSet,
  ctx: AssignmentRuleContext,
): string | undefined {
  const sorted = [...ruleSet.rules].sort((a, b) => a.sortOrder - b.sortOrder);

  // The split-type candidates to try, in order: the primary split type, then any
  // fallbacks, de-duplicated.
  const candidates: (string | undefined)[] = [];
  const seen = new Set<string | undefined>();
  for (const splitType of [ctx.splitType, ...(ctx.fallbackSplitTypes ?? [])]) {
    if (seen.has(splitType)) continue;
    seen.add(splitType);
    candidates.push(splitType);
  }

  // Pass 1 — full match. Try each candidate split type in order; the first rule
  // whose EVERY specified filter (incl. producer/buyer) matches wins. A catch-all
  // rule (no filters) matches the first candidate, so behavior is unchanged when
  // no fallbacks are supplied.
  for (const splitType of candidates) {
    for (const rule of sorted) {
      if (ruleMatches(rule, { ...ctx, splitType })) {
        return rule.questionnaireId;
      }
    }
  }

  // Pass 2 — size-safe rescue. When no rule fully matches, a size-scoped rule
  // that matches the claim's size (ignoring the producer/buyer audience) wins
  // over the size-agnostic `defaultQuestionnaireId`. This prevents a half/quarter
  // buyer from being stamped a whole-animal default just because the matching
  // size rule was scoped to a different producer/buyer (the default is set by the
  // legacy migration to a non-size-aware questionnaire). Audience-only rules (no
  // size filter) are intentionally excluded so their scoping still holds, and the
  // inspection-level filter is still enforced so a rescue never crosses it.
  for (const splitType of candidates) {
    for (const rule of sorted) {
      if (
        isSizeScoped(rule) &&
        ruleMatches(rule, { ...ctx, splitType }, { ignoreAudience: true })
      ) {
        return rule.questionnaireId;
      }
    }
  }

  return ruleSet.defaultQuestionnaireId;
}

/**
 * A rule with no filter dimensions matches every order context — it is a
 * catch-all. Empty arrays count as "no filter", mirroring {@link ruleMatches}.
 * @param rule
 */
export function isCatchAllRule(rule: PlainAssignmentRule): boolean {
  return (
    !rule.inspectionLevel?.length &&
    !rule.splitType?.length &&
    !rule.splitIndex?.length &&
    !rule.producer?.length &&
    !rule.buyer?.length
  );
}

/**
 * Returns true if the rule set is guaranteed to resolve a questionnaire for
 * ANY order context — i.e. it has a fallback `defaultQuestionnaireId`, or at
 * least one catch-all rule (a rule with no filters, which always matches).
 *
 * When this is false, some orders resolve to no questionnaire, which leaves the
 * buyer with nothing to fill out on the cutsheet page (SUP-224). Use it to
 * block saving a rule set that can silently strand buyers.
 * @param ruleSet
 */
export function ruleSetAlwaysResolves(
  ruleSet: PlainAssignmentRuleSet,
): boolean {
  return (
    Boolean(ruleSet.defaultQuestionnaireId) ||
    ruleSet.rules.some(isCatchAllRule)
  );
}
